import { IChatMessage } from '@/interfaces/deltaGpt/IChatMessage.ts';
import { IConversationSummary } from '@/interfaces/deltaGpt/IConversationSummary.ts';
import { IUserAiHistory } from '@/interfaces/deltaGpt/IUserAiHistory.ts';
import { useDeltaGptHistoryStore } from '@/stores/deltaGpt/deltaGptHistoryStore.ts';
import { computed, type Ref } from 'vue';

export function useConversationHistory (): {
    conversationHistory: Ref<Record<string, IConversationSummary>>;
    previousConversationMessages: Ref<IChatMessage[] | undefined>;
    redirectFromHistory: Ref<boolean>;
} {
    const deltaGptHistoryStore = useDeltaGptHistoryStore();
    const userHistoryItems = computed<Array<IConversationSummary> | null>(() => deltaGptHistoryStore.userHistory);
    const conversationMessagesForId = computed<Array<IUserAiHistory>>(() => deltaGptHistoryStore.currentChatMessageHistory);
    const redirectFromHistory = computed<boolean>(() => deltaGptHistoryStore.redirectFromHistory);

    const conversationHistory = computed<Record<string, IConversationSummary>>(() => {
        if (!userHistoryItems.value) return {};

        const historyDict: Record<string, IConversationSummary> = {};

        userHistoryItems.value.forEach(item => {
            const conversationId = item.conversationId;

            if (!historyDict[conversationId]) {
                historyDict[conversationId] = item;
            }
        });

        const sortedHistoryDict: Record<string, IConversationSummary> = {};

        Object.keys(historyDict)
            .sort((a, b) => {
                const dateA = new Date(historyDict[a].time);
                const dateB = new Date(historyDict[b].time);
                return dateB.getTime() - dateA.getTime();
            })
            .forEach(conversationId => {
                sortedHistoryDict[conversationId] = historyDict[conversationId];
            });

        return sortedHistoryDict;
    });

    const previousConversationMessages = computed<IChatMessage[] | undefined>(() => {
        if (!conversationMessagesForId.value || conversationMessagesForId.value.length === 0) {
            return undefined;
        }

        const messageStack: IChatMessage[] = [];
        conversationMessagesForId.value.forEach(message => {
            messageStack.push({ content: message.search.question, role: 'user' });
            messageStack.push({ content: message.search.answer, role: 'assistant' });
        });
        return messageStack;
    });

    return {
        conversationHistory,
        previousConversationMessages,
        redirectFromHistory,
    };
}
